// frontend/src/hooks/usePhotoInbox.ts
/**
 * React Query hooks for the selected tank's photo inbox
 * (docs/plans/2026-10-13-photo-inbox.md, 4.3, 4.4 and 7).
 *
 * Signed URLs have at least 70 minutes left when served, so pages and photos
 * stay fresh for 30 minutes; an image that still fails to load refetches its
 * query (useRefetchOnImageError). Assign and discard edit the cached list and
 * count rather than refetching them, and refresh only the organism the photo
 * went to; the livestock list (thumbnails) is refreshed once, when the user
 * leaves the inbox pages (useRefreshLivestockOnInboxExit, in ProtectedLayout).
 * A mutation's retries stop if another user signs in (or the user signs out)
 * before they are sent.
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type Query,
  type QueryClient,
  type QueryKey,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useTank } from '@/contexts/TankContext';
import {
  DEFAULT_BUSY_WAIT_S,
  photoInboxApi,
  PhotoInboxBusyError,
  PhotoInboxConflictError,
  PhotoInboxError,
} from '@/api/photoInbox';
import { RateLimitError } from '@/api/rateLimit';
import { tanksQueryKey } from '@/api/tanks';
import { getCachedSession } from '@/lib/supabase';
import {
  isTankResult,
  type AssignTarget,
  type PhotoInboxAssignRequest,
  type PhotoInboxAssignResult,
  type PhotoInboxCount,
  type PhotoInboxDetail,
  type PhotoInboxDiscardResponse,
  type PhotoInboxItem,
  type PhotoInboxPage,
} from '@/types/photoInbox';
import { insertIntoPages } from './photoInboxCache';
import { photoInboxKeys } from './photoInboxKeys';
import { tankPhotoKeys } from './tankPhotoKeys';
import { invalidateOrganismPhotos } from './useLivestockPhotos';

export const INBOX_PAGE_SIZE = 60;

/** The inbox grid and the assign view. */
export const INBOX_ROUTE = /^\/livestock\/photos(\/|$)/;

const PHOTO_STALE_MS = 30 * 60 * 1000;
const COUNT_STALE_MS = 60 * 1000;
/** A burst of failed images (a whole page of expired URLs) refetches once. */
const IMAGE_REFETCH_GAP_MS = 30 * 1000;
/** An assign of the same photo running elsewhere usually finishes within seconds. */
const IN_PROGRESS_RETRIES = 3;
const IN_PROGRESS_DELAY_MS = 2000;
/**
 * A 429 with a wait this short is the server's per-user cap on photos being
 * processed (uploads and cropped assigns, Retry-After 5), not the hourly
 * limiter: wait and send the assign again, a few times.
 */
const IN_FLIGHT_MAX_WAIT_S = 30;
const IN_FLIGHT_RETRIES = 5;

type InboxPages = InfiniteData<PhotoInboxPage, string | null>;

export interface InboxPhotoVariables {
  tankId: string;
  id: number;
}

export interface AssignInboxVariables extends InboxPhotoVariables {
  body: PhotoInboxAssignRequest;
}

export interface RestoreInboxVariables extends InboxPhotoVariables {
  /** Where an undone assign went; omitted (or null) when undoing a discard. */
  target?: AssignTarget | null;
  /** The organism an undone assign went to; omitted when undoing a discard or a tank shot. */
  livestockId?: number | null;
}

/** A retry not sent: another user signed in (or the user signed out) since the request started. */
export class InboxSessionChangedError extends Error {
  constructor() {
    super('Signed out');
    this.name = 'InboxSessionChangedError';
  }
}

function sessionUserId(): string | null {
  return getCachedSession()?.user?.id ?? null;
}

// Who was signed in when each mutation was first sent. TanStack Query hands
// every attempt of a mutation the same variables object.
const startedBy = new WeakMap<object, string | null>();

/** Sends the request only while the user who started the mutation is still signed in. */
function asStartingUser<V extends object, T>(send: (variables: V) => Promise<T>) {
  return (variables: V): Promise<T> => {
    const user = sessionUserId();
    if (!startedBy.has(variables)) startedBy.set(variables, user);
    else if (startedBy.get(variables) !== user) {
      return Promise.reject(new InboxSessionChangedError());
    }
    return send(variables);
  };
}

function useTankId(): string | undefined {
  const { selectedTank } = useTank();
  return selectedTank?.id;
}

function isNotFound(error: unknown): boolean {
  return error instanceof PhotoInboxError && error.status === 404;
}

/**
 * An assign's 404 is the photo (no longer pending) unless the server says the
 * organism wasn't found (deleted, or moved to another tank, meanwhile).
 */
export function isLivestockNotFound(error: unknown): boolean {
  return isNotFound(error) && /livestock/i.test((error as Error).message);
}

function isConflict(error: unknown, code: string): boolean {
  return error instanceof PhotoInboxConflictError && error.conflict.code === code;
}

/** Tank shots changed: the Tank Shots page and the tanks list (covers, shot counts). */
function tankShotsChanged(queryClient: QueryClient, tankId: string): void {
  void queryClient.invalidateQueries({ queryKey: tankPhotoKeys.all(tankId) });
  void queryClient.invalidateQueries({ queryKey: tanksQueryKey });
}

export function useInboxCount(): UseQueryResult<number, Error> {
  const tankId = useTankId();
  return useQuery({
    queryKey: photoInboxKeys.count(tankId ?? ''),
    queryFn: () => photoInboxApi.count(tankId as string),
    select: (data: PhotoInboxCount) => data.count,
    enabled: !!tankId,
    staleTime: COUNT_STALE_MS,
  });
}

/** Pending photos, oldest capture date first, a page of 60 at a time. */
export function useInboxList(): UseInfiniteQueryResult<InboxPages, Error> {
  const tankId = useTankId();
  return useInfiniteQuery({
    queryKey: photoInboxKeys.list(tankId ?? ''),
    queryFn: ({ pageParam }) =>
      photoInboxApi.list(tankId as string, { after: pageParam, limit: INBOX_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_after,
    enabled: !!tankId,
    staleTime: PHOTO_STALE_MS,
  });
}

function itemQuery(
  tankId: string,
  id: number
): {
  queryKey: ReturnType<typeof photoInboxKeys.item>;
  queryFn: () => Promise<PhotoInboxDetail>;
  staleTime: number;
} {
  return {
    queryKey: photoInboxKeys.item(tankId, id),
    queryFn: () => photoInboxApi.get(tankId, id),
    staleTime: PHOTO_STALE_MS,
  };
}

/** One pending photo with its 1920 px URL and neighbours; a 404 means it was sorted. */
export function useInboxItem(id: number | null): UseQueryResult<PhotoInboxDetail, Error> {
  const tankId = useTankId();
  return useQuery({
    ...itemQuery(tankId ?? '', id ?? 0),
    enabled: !!tankId && id !== null,
    retry: (failures, error) => !isNotFound(error) && failures < 1,
  });
}

/** Fetches a photo ahead (the next one) and has the browser load its full image. */
export function usePrefetchInboxItem(id: number | null): void {
  const tankId = useTankId();
  const queryClient = useQueryClient();
  const preloaded = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!tankId || id === null) return undefined;
    let cancelled = false;
    queryClient
      .fetchQuery(itemQuery(tankId, id))
      .then((photo) => {
        if (cancelled || !photo.full_url) return;
        const image = new Image();
        image.src = photo.full_url;
        preloaded.current = image;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [queryClient, tankId, id]);
}

/**
 * An <img> onError handler that refetches the query holding the signed URL,
 * at most once every 30 seconds.
 */
export function useRefetchOnImageError(refetch: () => Promise<unknown>): () => void {
  const last = useRef(0);
  return useCallback(() => {
    const now = Date.now();
    if (last.current && now - last.current < IMAGE_REFETCH_GAP_MS) return;
    last.current = now;
    void refetch();
  }, [refetch]);
}

/** Assigned photos since the app was opened, for "All sorted · 18 assigned this session". */
export function useInboxSessionCount(): number {
  const tankId = useTankId();
  const queryClient = useQueryClient();
  const queryKey = photoInboxKeys.session(tankId ?? '');
  const { data } = useQuery({
    queryKey,
    queryFn: () => queryClient.getQueryData<number>(queryKey) ?? 0,
    initialData: 0,
    // Read from the cache only: never fetched, so invalidating the tank's inbox keeps it.
    enabled: false,
  });
  return tankId ? data : 0;
}

function addToSession(queryClient: QueryClient, tankId: string, delta: number): void {
  queryClient.setQueryData<number>(photoInboxKeys.session(tankId), (count = 0) =>
    Math.max(0, count + delta)
  );
}

type Links = Pick<PhotoInboxDetail, 'prev_id' | 'next_id'>;

/**
 * Points a cached photo that links to a photo no longer pending past it, to
 * that photo's own neighbours; without them (it wasn't cached) the photo is
 * marked stale instead, so it is fetched again when shown.
 */
function unlink(
  queryClient: QueryClient,
  queryKey: QueryKey,
  id: number,
  gone: Links | null
): void {
  const photo = queryClient.getQueryData<PhotoInboxDetail>(queryKey);
  if (!photo || (photo.prev_id !== id && photo.next_id !== id)) return;
  if (!gone) {
    void queryClient.invalidateQueries({ queryKey, exact: true });
    return;
  }
  queryClient.setQueryData<PhotoInboxDetail>(queryKey, {
    ...photo,
    prev_id: photo.prev_id === id ? gone.prev_id : photo.prev_id,
    next_id: photo.next_id === id ? gone.next_id : photo.next_id,
  });
}

/** Unlinks once the answer on its way arrives, since it may predate the change. */
function unlinkWhenFetched(
  queryClient: QueryClient,
  query: Query,
  id: number,
  gone: Links | null
): void {
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.query !== query) return;
    if (event.type === 'removed') {
      unsubscribe();
    } else if (
      event.type === 'updated' &&
      (event.action.type === 'success' || event.action.type === 'error')
    ) {
      unsubscribe();
      unlink(queryClient, query.queryKey, id, gone);
    }
  });
}

/** Takes a photo that is no longer pending out of the cached pages and neighbours' links. */
function dropFromList(queryClient: QueryClient, tankId: string, id: number): void {
  queryClient.setQueryData<InboxPages>(photoInboxKeys.list(tankId), (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.filter((photo) => photo.id !== id),
          })),
        }
      : data
  );

  const itemKey = photoInboxKeys.item(tankId, id);
  const goneQuery = queryClient.getQueryCache().find({ queryKey: itemKey, exact: true });
  const gone = queryClient.getQueryData<PhotoInboxDetail>(itemKey) ?? null;
  // Stale for the next visit (a 404 then), but not refetched while the view still shows it.
  goneQuery?.invalidate();
  for (const query of queryClient
    .getQueryCache()
    .findAll({ queryKey: [...photoInboxKeys.tank(tankId), 'item'] })) {
    if (query === goneQuery) continue;
    unlink(queryClient, query.queryKey, id, gone);
    // A prefetch of the next photo, say, sent before this photo was sorted.
    if (query.state.fetchStatus === 'fetching') unlinkWhenFetched(queryClient, query, id, gone);
  }
}

/** The photo was assigned or discarded here: one fewer to sort. */
function resolved(queryClient: QueryClient, tankId: string, id: number): void {
  dropFromList(queryClient, tankId, id);
  queryClient.setQueryData<PhotoInboxCount>(photoInboxKeys.count(tankId), (data) =>
    data ? { count: Math.max(0, data.count - 1) } : data
  );
}

/** The photo turned out not to be pending (sorted elsewhere): drop it and recount. */
function goneElsewhere(queryClient: QueryClient, tankId: string, id: number): void {
  dropFromList(queryClient, tankId, id);
  void queryClient.invalidateQueries({ queryKey: photoInboxKeys.count(tankId) });
}

// Per query client: whether the user is on the inbox pages, and whether the
// livestock list (its thumbnails) changed while they were.
const inboxVisits = new WeakMap<QueryClient, { inInbox: boolean; livestockStale: boolean }>();

function livestockChanged(queryClient: QueryClient): void {
  const visit = inboxVisits.get(queryClient);
  if (visit && !visit.inInbox) {
    void queryClient.invalidateQueries({ queryKey: ['livestock'] });
    return;
  }
  inboxVisits.set(queryClient, { inInbox: visit?.inInbox ?? true, livestockStale: true });
}

/** Refreshes the livestock list once when the user leaves the inbox pages after assigning. */
export function useRefreshLivestockOnInboxExit(pathname: string): void {
  const queryClient = useQueryClient();
  const inInbox = INBOX_ROUTE.test(pathname);

  useEffect(() => {
    const visit = inboxVisits.get(queryClient);
    if (!inInbox && visit?.livestockStale) {
      void queryClient.invalidateQueries({ queryKey: ['livestock'] });
    }
    inboxVisits.set(queryClient, {
      inInbox,
      livestockStale: inInbox ? (visit?.livestockStale ?? false) : false,
    });
  }, [queryClient, inInbox]);
}

/** The per-user cap on photos being processed (see IN_FLIGHT_MAX_WAIT_S). */
function isInFlightCap(error: Error): error is RateLimitError {
  return (
    error instanceof RateLimitError &&
    error.retryAfterSeconds !== null &&
    error.retryAfterSeconds <= IN_FLIGHT_MAX_WAIT_S
  );
}

function assignRetry(failures: number, error: Error): boolean {
  if (error instanceof PhotoInboxBusyError) return failures < 1;
  if (isInFlightCap(error)) return failures < IN_FLIGHT_RETRIES;
  if (isConflict(error, 'in_progress')) return failures < IN_PROGRESS_RETRIES;
  return false;
}

function assignRetryDelay(_failures: number, error: Error): number {
  if (error instanceof PhotoInboxBusyError) {
    return (error.retryAfterSeconds ?? DEFAULT_BUSY_WAIT_S) * 1000;
  }
  if (isInFlightCap(error)) return (error.retryAfterSeconds ?? 0) * 1000;
  return IN_PROGRESS_DELAY_MS;
}

/**
 * Makes a photo a timeline entry or a tank shot. A busy server (cropped assign) is retried
 * once after its Retry-After, too many of the user's photos being processed
 * (a 429 with a short Retry-After) a few times after it, and an assign in
 * progress elsewhere a few times; anything else is the caller's to show, with
 * the user's choices kept.
 */
export function useAssignInboxPhoto(): UseMutationResult<
  PhotoInboxAssignResult,
  Error,
  AssignInboxVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: photoInboxKeys.assignMutation,
    mutationFn: asStartingUser(({ tankId, id, body }: AssignInboxVariables) =>
      photoInboxApi.assign(tankId, id, body)
    ),
    // Fail rather than wait while the browser thinks it is offline: the view offers Retry.
    networkMode: 'always',
    retry: assignRetry,
    retryDelay: assignRetryDelay,
    onSuccess: (result, { tankId, id }) => {
      resolved(queryClient, tankId, id);
      addToSession(queryClient, tankId, 1);
      if (isTankResult(result)) {
        tankShotsChanged(queryClient, tankId);
        return;
      }
      // Its timeline, compare index and detail (livestock compare plan 5.1)
      invalidateOrganismPhotos(queryClient, tankId, result.livestock_id);
      livestockChanged(queryClient);
    },
    onError: (error, { tankId, id }) => {
      if (isLivestockNotFound(error)) {
        void queryClient.invalidateQueries({ queryKey: ['livestock', tankId] });
      } else if (isNotFound(error) || isConflict(error, 'assigned_elsewhere')) {
        // Sorted already, to the other target: no longer one to sort here.
        goneElsewhere(queryClient, tankId, id);
      }
    },
  });
}

export function useDiscardInboxPhoto(): UseMutationResult<
  PhotoInboxDiscardResponse,
  Error,
  InboxPhotoVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: photoInboxKeys.discardMutation,
    mutationFn: asStartingUser(({ tankId, id }: InboxPhotoVariables) =>
      photoInboxApi.discard(tankId, id)
    ),
    networkMode: 'always',
    onSuccess: (_result, { tankId, id }) => resolved(queryClient, tankId, id),
    onError: (error, { tankId, id }) => {
      if (isNotFound(error) || isConflict(error, 'not_pending')) {
        goneElsewhere(queryClient, tankId, id);
      }
    },
  });
}

/**
 * Undo of an assign or discard; an undone tank shot also refreshes Tank
 * Shots and the tanks list. The photo goes back into the grid's cached
 * pages in its place, rather than every loaded page being fetched again, and
 * the count is fetched. Cached photos' links skip it now, so they are fetched
 * again when shown (the assign view loads the restored photo itself: the
 * answer has no full URL).
 */
export function useRestoreInboxPhoto(): UseMutationResult<
  PhotoInboxItem,
  Error,
  RestoreInboxVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: asStartingUser(({ tankId, id }: RestoreInboxVariables) =>
      photoInboxApi.restore(tankId, id)
    ),
    networkMode: 'always',
    retry: (failures, error) => isConflict(error, 'in_progress') && failures < IN_PROGRESS_RETRIES,
    retryDelay: IN_PROGRESS_DELAY_MS,
    onSuccess: (photo, { tankId, target, livestockId }) => {
      if (target === 'tank') {
        addToSession(queryClient, tankId, -1);
        tankShotsChanged(queryClient, tankId);
      } else if (livestockId) {
        addToSession(queryClient, tankId, -1);
        // The entry is gone: its timeline, compare index and detail (it may
        // have been the reference) follow
        invalidateOrganismPhotos(queryClient, tankId, livestockId);
        livestockChanged(queryClient);
      }
      insertIntoPages(queryClient, tankId, photo);
      void queryClient.invalidateQueries({ queryKey: photoInboxKeys.count(tankId), exact: true });
      void queryClient.invalidateQueries({
        queryKey: [...photoInboxKeys.tank(tankId), 'item'],
        refetchType: 'none',
      });
    },
  });
}
